import { Component, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { ClippingStudioNavigationService } from "../../../infrastructure/clipping-studio/clipping-studio-navigation.service";
import {
  ColDef,
  GridApi,
  GridOptions,
  IDatasource,
  IGetRowsParams,
} from "ag-grid-community";

import { MamExplorerStateService } from "../../../services/mam-explorer/mam-explorer-state.service";
import { MediaRepository } from "../../../domain/media-repository";
import {
  MamAsset,
  MamStatus,
  isVideoMediaItem,
  mapMediaItemToMamAsset,
} from "../../../domain/mam-explorer";
import { MediaItem } from "../../../domain/media";
import { CsvExportService } from "../../../services/csv-export.service";

import { AgGridEvrideoCheckboxCellComponent } from "../../common/ag-grid-evrideo/ag-grid-evrideo-checkbox-cell/ag-grid-evrideo-checkbox-cell.component";
import { MamExplorerSelectAllHeaderComponent } from "../mam-explorer-select-all-header/mam-explorer-select-all-header.component";
import { MamExplorerNameCellComponent } from "../mam-explorer-name-cell/mam-explorer-name-cell.component";
import { AgGridEvrideoCustomLoadingOverlayComponent } from "../../common/ag-grid-evrideo/ag-grid-evrideo-custom-loading-overlay/ag-grid-evrideo-custom-loading-overlay.component";
import { MamExplorerActionsCellComponent } from "../mam-explorer-actions-cell/mam-explorer-actions-cell.component";
import { MamExplorerStatusCellComponent } from "../mam-explorer-status-cell/mam-explorer-status-cell.component";
import { SOURCE_LABELS, TYPE_LABELS } from "../mam-explorer.constants";

@Component({
  selector: "evrideo-mam-explorer-container",
  templateUrl: "./mam-explorer-container.component.html",
  styleUrls: ["./mam-explorer-container.component.scss"],
})
export class MamExplorerContainerComponent implements OnInit {
  /** Matches MediaRepository.getMedia hardcoded Limit=20. */
  public readonly pageSize = 20;
  public selectedTableItemsCount = 0;
  public gridApi: GridApi;
  public gridColumnApi: any;

  /**
   * Client-side video fill cursor. API Offset is independent of AG Grid
   * startRow because GetMediaList cannot filter by ContentMediaType.
   */
  private apiOffset = 0;
  private apiExhausted = false;
  private filteredBuffer: MamAsset[] = [];
  private requestGeneration = 0;

  public overlayNoRowsTemplate = `<div style="display:flex;flex-direction:column;align-items:center;padding:40px;">
    <h2 style="color:#888;">No media assets found. Try adjusting your search or filters.</h2>
  </div>`;

  columnDefs: ColDef[] = [
    {
      resizable: false,
      pinned: "left",
      width: 36,
      minWidth: 36,
      maxWidth: 36,
      cellRenderer: AgGridEvrideoCheckboxCellComponent,
      headerComponent: MamExplorerSelectAllHeaderComponent,
      headerClass: "mam-explorer__checkbox-header-cell",
      cellClass: "mam-explorer__checkbox-cell",
      cellStyle: { padding: 0 },
    },
    {
      headerName: "Name",
      flex: 1,
      cellRenderer: MamExplorerNameCellComponent,
      cellClass: ["ag-grid-evrideo-default-cell-class", "mam-explorer__name-cell"],
      cellRendererParams: {
        videoName: (data: MamAsset) => data?.title || "",
        videoThumb: (data: MamAsset) =>
          data?.thumbnailUrl || "/assets/icons/video-icon.svg",
      },
    },
    {
      headerName: "Video ID",
      field: "videoId",
      flex: 0,
      width: 280,
      minWidth: 200,
      maxWidth: 280,
      cellClass: [
        "ag-grid-evrideo-default-cell-class",
        "mam-explorer__video-id-cell",
      ],
    },
    {
      headerName: "Duration",
      field: "durationFormatted",
      maxWidth: 120,
    },
    {
      headerName: "Type",
      field: "type",
      maxWidth: 100,
      cellRenderer: (params: any) => {
        if (!params.data) return "";
        const label = TYPE_LABELS[params.data.type] || params.data.type;
        return `<span style="display:inline-block;padding:2px 8px;border-radius:10px;font-size:11px;font-weight:600;">${label}</span>`;
      },
    },
    {
      headerName: "Status",
      field: "status",
      maxWidth: 120,
      cellRenderer: MamExplorerStatusCellComponent,
    },
    {
      headerName: "Source",
      field: "source",
      maxWidth: 120,
      cellRenderer: (params: any) => {
        if (!params.data) return "";
        return SOURCE_LABELS[params.data.source] || params.data.source;
      },
    },
    {
      headerName: "Created",
      field: "createdAt",
      maxWidth: 140,
      cellRenderer: (params: any) => {
        if (!params.data) return "";
        return params.data.createdAt || "—";
      },
    },
    {
      headerName: "",
      maxWidth: 120,
      resizable: false,
      pinned: "right",
      cellRenderer: MamExplorerActionsCellComponent,
    },
  ];

  dataSource: IDatasource = {
    rowCount: undefined,
    getRows: async (params: IGetRowsParams) => {
      const component = params.context.componentParent as MamExplorerContainerComponent;
      let rowCount = params.startRow;
      const generation = component.requestGeneration;

      try {
        component.manuallyHideOverlay();
        component.manuallyShowLoadingOverlay();

        await component.fillFilteredBufferUntil(params.endRow, generation);
        if (generation !== component.requestGeneration) {
          params.failCallback();
          return;
        }

        const assets = component.filteredBuffer.slice(
          params.startRow,
          params.endRow,
        );

        component.state.setTotalCount(component.filteredBuffer.length);
        component.state.setReadyCount(
          component.filteredBuffer.filter((a) => a.status === MamStatus.Ready)
            .length,
        );
        component.state.setProcessingCount(
          component.filteredBuffer.filter(
            (a) => a.status === MamStatus.Uploading,
          ).length,
        );

        if (component.apiExhausted) {
          rowCount = component.filteredBuffer.length;
        } else if (component.filteredBuffer.length === 0) {
          rowCount = 0;
        } else {
          // Tell AG Grid more rows may exist beyond the current buffer.
          rowCount = component.filteredBuffer.length + 1;
        }

        params.successCallback(assets, rowCount);
      } catch {
        params.failCallback();
      } finally {
        if (generation === component.requestGeneration) {
          component.manuallyHideOverlay();
          if (component.gridApi && rowCount === 0) {
            setTimeout(() => component.gridApi?.showNoRowsOverlay());
          }
        }
      }
    },
  };

  gridOptions: GridOptions;

  constructor(
    public state: MamExplorerStateService,
    private mediaRepository: MediaRepository,
    private clippingStudioNavigation: ClippingStudioNavigationService,
    private csvExportService: CsvExportService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.gridOptions = <GridOptions>{
      context: {
        componentParent: this,
      },
      rowModelType: "infinite",
      columnDefs: this.columnDefs,
      defaultColDef: {
        resizable: true,
        lockPosition: true,
        sortable: false,
        suppressHeaderMenuButton: true,
        width: 155,
        flex: 1,
        cellClass: "ag-grid-evrideo-default-cell-class",
      } as ColDef,
      rowHeight: 44,
      suppressHorizontalScroll: true,
      suppressRowClickSelection: true,
      animateRows: true,
      cacheBlockSize: this.pageSize,
      pagination: false,
      alwaysShowVerticalScroll: true,
      enableCellTextSelection: true,
      rowSelection: "multiple",
      getRowHeight: () => 44,
      datasource: this.dataSource,
      maxConcurrentDatasourceRequests: 1,
      loadingOverlayComponent: AgGridEvrideoCustomLoadingOverlayComponent,
      overlayNoRowsTemplate: this.overlayNoRowsTemplate,
    };
  }

  // ── Grid event handlers ──────────────────────────────────

  onGridReady(params: any): void {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
    this.gridApi.sizeColumnsToFit();
  }

  onSelectionChanged(event: any): void {
    const nodes = event.api.getSelectedNodes();
    this.selectedTableItemsCount = nodes.length;
    const ids = new Set<number>(
      nodes.map((n: any) => n.data?.id).filter(Boolean),
    );
    this.state.setSelectedIds(ids);
  }

  doSearch(): void {
    this.resetFilteredSession();
    if (this.gridApi) {
      this.gridApi.setGridOption("datasource", this.dataSource);
      this.manuallyShowLoadingOverlay();
    }
  }

  manuallyShowLoadingOverlay(): void {
    if (this.gridApi) {
      setTimeout(() => this.gridApi.showLoadingOverlay());
    }
  }

  manuallyHideOverlay(): void {
    if (this.gridApi) {
      setTimeout(() => this.gridApi.hideOverlay());
    }
  }

  // ── Toolbar handlers ────────────────────────────────────

  onSearchChange(query: string): void {
    this.state.setSearchQuery(query);
    this.doSearch();
  }

  onStatusFilterChange(status: MamStatus | "All"): void {
    this.state.setStatusFilter(status);
    this.doSearch();
  }

  onUploadClick(): void {
    this.router.navigate(["/mam", "uploadReport"]);
  }

  onExportClick(): void {
    if (!this.gridApi) return;
    const assets: MamAsset[] = [];
    this.gridApi.forEachNode((node) => {
      if (node.data) assets.push(node.data);
    });
    if (!assets.length) return;
    const rows = assets.map((a) => ({
      ID: a.id,
      VideoID: a.videoId,
      Title: a.title,
      Duration: a.durationFormatted,
      Type: a.type,
      Status: a.status,
      Source: a.source,
      Created: a.createdAt || "",
    }));
    this.csvExportService.exportToCsv("mam-assets.csv", rows);
  }

  // ── Row action handlers (called from cell renderer) ────

  onEdit(id: number): void {
    this.router.navigate(["/mam/editMedia", id]);
  }

  onPlay(id: number): void {
    this.mediaRepository.getMediaPreviewUrl(id).then((url) => {
      if (url) {
        window.open(url, "_blank");
      }
    });
  }

  onDownload(id: number): void {
    this.mediaRepository.downloadTranscodedFile(id);
  }

  // ── Bulk action handlers ────────────────────────────────

  private getSelectedAssets(): MamAsset[] {
    if (!this.gridApi) return [];
    return this.gridApi
      .getSelectedNodes()
      .map((n) => n.data as MamAsset)
      .filter(Boolean);
  }

  onBulkDownload(): void {
    const assets = this.getSelectedAssets();
    assets.forEach((a) => this.mediaRepository.downloadOriginalFile(a.id));
  }

  onBulkExportMetadata(): void {
    const assets = this.getSelectedAssets();
    if (!assets.length) return;
    const rows = assets.map((a) => ({
      ID: a.id,
      VideoID: a.videoId,
      Title: a.title,
      Duration: a.durationFormatted,
      Type: a.type,
      Status: a.status,
      Source: a.source,
      Created: a.createdAt || "",
    }));
    this.csvExportService.exportToCsv("mam-selected-metadata.csv", rows);
  }

  async onBulkDelete(): Promise<void> {
    const assets = this.getSelectedAssets();
    if (!assets.length) return;

    const confirmed = window.confirm(
      `Delete ${assets.length} selected asset(s)? This action may be irreversible.`,
    );
    if (!confirmed) return;

    const errors: number[] = [];
    for (const asset of assets) {
      try {
        await this.mediaRepository.deleteMedia(asset.id);
      } catch {
        errors.push(asset.id);
      }
    }

    if (errors.length > 0) {
      alert(
        `Failed to delete ${errors.length} asset(s). IDs: ${errors.join(", ")}`,
      );
    }

    this.state.clearSelection();
    this.selectedTableItemsCount = 0;
    this.doSearch();
  }

  onBulkEditInStudio(): void {
    const assets = this.getSelectedAssets();
    if (!assets.length) return;
    void this.clippingStudioNavigation.openNewProjectForMedia(assets[0].id);
  }

  // ── Filtered infinite scroll ─────────────────────────────

  private resetFilteredSession(): void {
    this.requestGeneration += 1;
    this.apiOffset = 0;
    this.apiExhausted = false;
    this.filteredBuffer = [];
  }

  /**
   * Fetches GetMediaList pages until the filtered video buffer covers
   * endRow or the API reports no further pages.
   */
  private async fillFilteredBufferUntil(
    endRow: number,
    generation: number,
  ): Promise<void> {
    while (
      this.filteredBuffer.length < endRow &&
      !this.apiExhausted &&
      generation === this.requestGeneration
    ) {
      const statusParam = this.mapStatusToApiParam(this.state.statusFilter());
      const items = await this.mediaRepository.getMedia(
        this.state.searchQuery(),
        this.apiOffset,
        1, // orderByMedia
        "DateCreated", // sortByMedia
        null, // startDateCreatedMedia
        null, // endDateCreatedMedia
        null, // startDateLicense
        null, // endDateLicense
        statusParam,
        [], // countries
        [], // tagIds
        [], // assetTypeIds
        [], // languages
      );

      if (generation !== this.requestGeneration) {
        return;
      }

      this.apiOffset += items.length;
      if (items.length < this.pageSize) {
        this.apiExhausted = true;
      }

      const mapped = this.mapAndFilterMediaItems(items);
      if (mapped.length > 0) {
        this.filteredBuffer = this.filteredBuffer.concat(mapped);
      }

      // Empty page with full Limit should not happen often; treat as progress
      // via apiOffset. Zero-length response exhausts the cursor.
      if (items.length === 0) {
        this.apiExhausted = true;
      }
    }
  }

  private mapAndFilterMediaItems(items: MediaItem[]): MamAsset[] {
    return items
      .filter((item) => isVideoMediaItem(item.contentMediaType))
      .map(mapMediaItemToMamAsset);
  }

  // ── Helpers ─────────────────────────────────────────────

  /** Values must match backend MediaStatusFilter: All, Ready, Uploading. */
  private mapStatusToApiParam(status: MamStatus | "All"): string {
    switch (status) {
      case MamStatus.Ready:
        return "Ready";
      case MamStatus.Uploading:
        return "Uploading";
      default:
        return "All";
    }
  }
}
